import React, { useEffect } from "react";
import { motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import { FiAward } from "react-icons/fi";
import { IconBaseProps } from "react-icons";

type BrandColor = "red" | "yellow" | "blue" | "pink" | "green" | "orange";

const AwardIcon = (props: IconBaseProps) =>
  React.createElement(FiAward as any, props);

export interface GovernmentFundedSpacesProps {
  title?: string;
  highlight?: string;
  footnote?: string;
  accent?: BrandColor;
  periodicAttention?: boolean;
  shimmerHeading?: boolean;
  icon?: React.ReactNode;
  aos?: string;
  className?: string;
}

const brandPalette: Record<BrandColor, string> = {
  red: "#ef4444",
  yellow: "#f59e0b",
  blue: "#3b82f6",
  pink: "#ec4899",
  green: "#10b981",
  orange: "#f97316",
};

const gradient = `linear-gradient(90deg,#ef4444,#f59e0b,#3b82f6,#ec4899,#10b981,#f97316)`;

function hexToRgba(hex: string, alpha = 0.14) {
  const n = hex.replace("#", "");
  const bigint = parseInt(n.length === 3 ? n.split("").map(c=>c+c).join("") : n, 16);
  const r = (bigint >> 16) & 255, g = (bigint >> 8) & 255, b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const GovernmentFundedSpaces: React.FC<GovernmentFundedSpacesProps> = ({
  title = "GOVERNMENT FUNDED HOURS",
  highlight = "FREE of charge to parents*!",
  footnote = "*Additional costs only apply if parents choose meals or other consumables.",
  accent = "blue",
  periodicAttention = true,
  shimmerHeading = true,
  icon,
  aos = "fade-up",
  className = "",
}) => {
  useEffect(() => {
    AOS.init({ once: true, offset: 80, duration: 600, easing: "ease-out" });
  }, []);

  const accentColor = brandPalette[accent];
  const ring = hexToRgba(accentColor, 0.16); // slightly stronger so it’s visible

  return (
    <>
      <style>{`
        @keyframes gfGlow {
          0%, 100% { box-shadow: 0 0 0 0 ${ring}; }
          50% { box-shadow: 0 0 0 8px ${ring}; }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0, 0 0; }
          100% { background-position: 200% 0, 0 0; }
        }
        .gf-card { position: relative; background: #fff; }
        .gf-topbar { position:absolute; top:-1px; left:0; right:0; height:3px; background:${gradient};
          border-top-left-radius:0.75rem; border-top-right-radius:0.75rem; }
        /* Always-readable base text + shimmer sweep layer */
        .gf-shimmer {
          background-image:
            linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,.9) 50%, rgba(255,255,255,0) 65%, rgba(255,255,255,0) 100%),
            linear-gradient(90deg, #111827, #111827); /* slate-900 base */
          background-size: 200% 100%, 100% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent; /* reveal the two BG layers */
          animation: shimmer 2.2s linear infinite;
        }
        .gf-glow { animation: gfGlow 8s ease-in-out infinite; animation-delay: 1.5s; }
        @media (prefers-reduced-motion: reduce) {
          .gf-glow { animation: none !important; }
          .gf-shimmer { animation: none !important; background-image: none !important; color: inherit !important; }
        }
        @media (prefers-color-scheme: dark) {
          .gf-card { background:#0b0b0c; }
          .gf-shimmer {
            background-image:
              linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,.9) 50%, rgba(255,255,255,0) 65%, rgba(255,255,255,0) 100%),
              linear-gradient(90deg, #f9fafb, #f9fafb); /* light text base */
          }
        }
      `}</style>

      <motion.section
        data-aos={aos}
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.6 }}
        transition={{ type: "tween", duration: 0.5 }}
        className={[
          "relative mx-auto max-w-3xl rounded-xl border",
          "p-4 sm:p-5 md:p-7 text-center",
          "border-gray-200 dark:border-white/10",
          periodicAttention ? "gf-glow" : "",
          "gf-card",
          className,
        ].join(" ")}
        aria-label="Government funded spaces"
      >
        <div className="gf-topbar" aria-hidden="true" />

        {/* Icon + title */}
        <div className="flex items-center justify-center gap-2 mb-2">
          <span
            className="inline-flex h-8 w-8 items-center justify-center rounded-full"
            style={{ background: "rgba(0,0,0,0.04)", outline: `2px solid ${accentColor}20` }}
            aria-hidden="true"
          >
            <span
              className="absolute h-8 w-8 rounded-full -z-10"
              style={{ background: gradient, filter: "blur(10px)", opacity: 0.15 }}
              aria-hidden="true"
            />
            <span className="relative">
              {icon ?? <AwardIcon size={18} color={accentColor} aria-hidden="true" />}
            </span>
          </span>

          <p className="m-0 text-xs sm:text-sm uppercase tracking-wide opacity-80 font-medium">
            {title}
          </p>
        </div>

        {/* Highlight — now readable + shimmering */}
        <h2 className="m-0 text-xl sm:text-2xl md:text-3xl font-semibold">
          <span className={shimmerHeading ? "gf-shimmer" : ""}>{highlight}</span>
        </h2>

        <p className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          {footnote}
        </p>

        {/* Gradient edge */}
        <div
          className="pointer-events-none absolute inset-0 -z-10 rounded-xl p-px"
          style={{ background: gradient, opacity: 0.25 }}
          aria-hidden="true"
        />
      </motion.section>
    </>
  );
};

export default GovernmentFundedSpaces;
